import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGradeInfo } from '../utils/gradeDisplay';
import { translateLeague } from '../utils/leagueNames';
import { settleUltimateV4 } from '../utils/ultimateSettle';

function translatePickType(pickType, t) {
  if (!pickType) return '?';
  if (pickType === 'Home Win') return t('homeWin');
  if (pickType === 'Away Win') return t('awayWin');
  if (pickType === 'Draw') return t('drawShort');
  if (pickType === 'BTTS Yes') return t('bttsYes');
  if (pickType === 'BTTS No') return t('bttsNo');
  if (pickType.startsWith('CS ') || pickType.startsWith('CS: ')) {
    const score = pickType.replace('CS ', '').replace('CS: ', '');
    return t('csPrefix') + score;
  }
  if (pickType === 'Over 2.5') return t('over25Pick');
  return pickType;
}

function translateSourceType(sourceType, t) {
  if (!sourceType) return '?';
  const map = {
    'outcome': t('sourceOutcome'),
    'btts': t('sourceBtts'),
    'draw': t('sourceDraw'),
    'correct_score': t('sourceCorrectScore'),
    'over25': t('sourceOver25'),
    'htft': t('sourceHtft'),
    'ultimate': t('sourceUltimate'),
  };
  return map[sourceType] || sourceType;
}

function translatePrimaryLabel(label, primary, t) {
  if (!label) return primary === 'draw' ? t('drawShort') : 'CS';
  if (label === 'Draw') return t('drawShort');
  if (label.startsWith('CS: ') || label.startsWith('CS ')) {
    const score = label.replace('CS: ', '').replace('CS ', '');
    return t('csPrefix') + score;
  }
  return label;
}

const PRIMARY_STYLES = {
  draw: { pill: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', box: 'bg-cyan-500/20 text-cyan-400' },
  btts: { pill: 'bg-blue-500/20 text-blue-400 border-blue-500/30', box: 'bg-blue-500/20 text-blue-400' },
  over25: { pill: 'bg-sky-500/20 text-sky-400 border-sky-500/30', box: 'bg-sky-500/20 text-sky-400' },
  outcome: { pill: 'bg-orange-500/20 text-orange-400 border-orange-500/30', box: 'bg-orange-500/20 text-orange-400' },
  correct_score: { pill: 'bg-amber-500/20 text-amber-400 border-amber-500/30', box: 'bg-amber-500/20 text-amber-400' },
};

function ultimateLabel(prediction, t) {
  if (prediction.schema_version === 4) return translatePickType(prediction.primary_label, t);
  return translatePrimaryLabel(prediction.primary_label, prediction.primary_pick, t);
}

function ultimateOdds(prediction) {
  if (prediction.schema_version === 4) return prediction.odds;
  return prediction.primary_pick === 'draw' ? prediction.draw_data?.odds_draw : prediction.cs_data?.estimated_fair_odds;
}

const SCRIPT_KEYS = { goals: 'scriptGoals', dominance: 'scriptDominance', stalemate: 'scriptStalemate' };

function PredictionCard({ prediction, type, oddsFormat = 'decimal', isFavorite, onToggleFavorite }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t, i18n } = useTranslation('dashboard');
  const isWin = checkWin(prediction, type);
  const isFinished = prediction.result?.status === 'FINISHED';
  const isLive = ['IN_PLAY', 'HALF_TIME', 'PAUSED', 'HALFTIME'].includes(prediction.result?.status);
  const isPostponed = prediction.result?.status === 'POSTPONED' || prediction.status === 'POSTPONED';

  const result = prediction.result;
  const hasScore = result && (result.status === 'FINISHED' || isLive);

  // Base styling based on result
  let statusStyle = 'border-anthracite-700';
  let bgStyle = '';
  if (isPostponed) {
    statusStyle = 'border-yellow-500/30';
    bgStyle = 'bg-yellow-500/5';
  } else if (isLive) {
    statusStyle = 'border-accent/30';
    bgStyle = 'bg-accent/5';
  } else if (isFinished && isWin !== null) {
    statusStyle = isWin ? 'border-win/30' : 'border-loss/30';
    bgStyle = isWin ? 'bg-win/5' : 'bg-loss/5';
  } else if (isFinished && isWin === null) {
    statusStyle = 'border-gray-500/30';
    bgStyle = 'bg-gray-500/5';
  }

  return (
    <div className={`border-b ${statusStyle} ${bgStyle}`}>
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center px-4 py-3 gap-4">
        {onToggleFavorite && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(prediction.id, prediction._originalType || type); }}
            className="w-6 flex-shrink-0 flex items-center justify-center"
          >
            <svg className={`w-4 h-4 transition-colors ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600 hover:text-gray-400'}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} fill={isFavorite ? 'currentColor' : 'none'}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        )}
        <div className="w-24 flex-shrink-0">
          <StatusBadge isPostponed={isPostponed} isLive={isLive} isFinished={isFinished} isWin={isWin} dateTime={prediction.date_time} lang={i18n.language} elapsedMinutes={result?.elapsedMinutes} matchTime={result?.matchTime} status={result?.status} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{prediction.home_team}</span>
            <span className="text-xs text-gray-500">vs</span>
            <span className="text-sm font-medium text-white truncate">{prediction.away_team}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-500">{translateLeague(prediction.league, i18n.language)}</span>
            {prediction.tags?.map((tag, idx) => (
              <span key={idx} className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${getTagStyle(tag)}`}>{tag}</span>
            ))}
          </div>
        </div>
        <div className="w-16 text-center flex-shrink-0">
          <ScoreDisplay hasScore={hasScore} isPostponed={isPostponed} isLive={isLive} isWin={isWin} isFinished={isFinished} result={result} />
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {type === 'btts' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <StatPill label={t('odds')} value={formatOdds(prediction.odds_btts, oddsFormat)} />
            </>
          ) : type === 'draw' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <PredictionPill prediction="D" label="DRAW" />
              <StatPill label={t('odds')} value={formatOdds(prediction.odds_draw, oddsFormat)} />
            </>
          ) : type === 'correct_score' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <ScorePill score={prediction.predicted_score} />
              <StatPill label={t('prob')} value={`${prediction.score_probability?.toFixed(1)}%`} />
            </>
          ) : type === 'over25' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <StatPill label={t('odds')} value={formatOdds(prediction.odds_over25, oddsFormat)} />
              <StatPill label={t('gpg')} value={prediction.total_gpg?.toFixed(2)} />
            </>
          ) : type === 'htft' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <HtftPredictionPill prediction={prediction.prediction} />
              <StatPill label={t('prob')} value={`${(prediction.htft_prob || 0).toFixed(1)}%`} />
              <StatPill label={t('odds')} value={formatOdds(prediction.odds, oddsFormat)} />
            </>
          ) : type === 'ultimate' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <PrimaryPickPill prediction={prediction} />
              <StatPill label={t('odds')} value={formatOdds(
                ultimateOdds(prediction),
                oddsFormat
              )} />
            </>
          ) : type === 'ai_picks' ? (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <AiPickTypePill prediction={prediction} t={t} />
              <StatPill label={t('conv')} value={prediction.conviction_score?.toFixed(1)} />
              <StatPill label={t('odds')} value={formatOdds(prediction.odds, oddsFormat)} />
            </>
          ) : (
            <>
              <GradeBadge prediction={prediction} type={type} />
              <PredictionPill prediction={prediction.prediction} label={prediction.prediction_label} />
              <StatPill label={t('odds')} value={formatOdds(prediction.odds, oddsFormat)} />
            </>
          )}
        </div>
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex-shrink-0 p-1 rounded hover:bg-anthracite-700">
          <svg className={`w-4 h-4 text-gray-500 hover:text-accent transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Mobile Layout - Compact two-column design */}
      <div className="md:hidden px-3 py-2.5" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex gap-3">
          {/* Star icon */}
          {onToggleFavorite && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(prediction.id, prediction._originalType || type); }}
              className="flex-shrink-0 flex items-start pt-0.5"
            >
              <svg className={`w-4 h-4 transition-colors ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} fill={isFavorite ? 'currentColor' : 'none'}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
          )}
          {/* Left: Teams & Info */}
          <div className="flex-1 min-w-0">
            {/* Teams with scores inline if finished */}
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[13px] font-medium text-white truncate">{prediction.home_team}</span>
              {hasScore && <span className={`text-[13px] font-bold ${isLive ? 'text-accent' : isWin === true ? 'text-win' : isWin === false ? 'text-loss' : 'text-gray-300'}`}>{result.homeGoals}</span>}
            </div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[13px] font-medium text-white truncate">{prediction.away_team}</span>
              {hasScore && <span className={`text-[13px] font-bold ${isLive ? 'text-accent' : isWin === true ? 'text-win' : isWin === false ? 'text-loss' : 'text-gray-300'}`}>{result.awayGoals}</span>}
            </div>
            {/* Status row */}
            <div className="flex items-center gap-2">
              {isPostponed ? (
                <span className="text-[10px] font-medium text-yellow-500">{t('postponed')}</span>
              ) : isLive ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-accent/20 rounded text-[10px] font-medium text-accent">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                  {result?.elapsedMinutes ? `${result.elapsedMinutes}'` : t('live')}
                </span>
              ) : isFinished && isWin !== null ? (
                <span className={`text-[10px] font-medium ${isWin ? 'text-win' : 'text-loss'}`}>{isWin ? t('win') : t('loss')}</span>
              ) : isFinished && isWin === null ? (
                <span className="text-[10px] font-medium text-gray-400">FT</span>
              ) : (
                <span className="text-[10px] text-gray-500">{formatTime(prediction.date_time, i18n.language)}</span>
              )}
              {prediction.tags && prediction.tags.length > 0 && (
                <div className="flex gap-1">
                  {prediction.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className={`px-1 py-0.5 rounded text-[8px] font-semibold ${getTagStyle(tag)}`}>{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Stats boxes */}
          <div className="flex-shrink-0">
            {type === 'btts' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniStatBox label={t('odds')} value={formatOdds(prediction.odds_btts, oddsFormat)} />
              </div>
            ) : type === 'draw' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniPredictionBox prediction="D" />
                <MiniStatBox label={t('odds')} value={formatOdds(prediction.odds_draw, oddsFormat)} />
              </div>
            ) : type === 'correct_score' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniScoreBox score={prediction.predicted_score} />
                <MiniStatBox label={t('prob')} value={`${prediction.score_probability?.toFixed(1)}%`} />
              </div>
            ) : type === 'over25' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniStatBox label={t('odds')} value={formatOdds(prediction.odds_over25, oddsFormat)} />
                <MiniStatBox label={t('gpg')} value={prediction.total_gpg?.toFixed(1)} />
              </div>
            ) : type === 'htft' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniHtftBox prediction={prediction.prediction} />
                <MiniStatBox label={t('prob')} value={`${(prediction.htft_prob || 0).toFixed(0)}%`} />
              </div>
            ) : type === 'ultimate' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniPrimaryPickBox prediction={prediction} />
                <MiniStatBox label={t('odds')} value={formatOdds(
                  ultimateOdds(prediction),
                  oddsFormat
                )} />
              </div>
            ) : type === 'ai_picks' ? (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniAiPickBox prediction={prediction} t={t} />
                <MiniStatBox label={t('conv')} value={prediction.conviction_score?.toFixed(1)} />
              </div>
            ) : (
              <div className="flex gap-1">
                <MiniGradeBadge prediction={prediction} type={type} />
                <MiniPredictionBox prediction={prediction.prediction} />
                <MiniStatBox label={t('odds')} value={formatOdds(prediction.odds, oddsFormat)} />
              </div>
            )}
            {/* Expand indicator */}
            <div className="flex justify-end mt-1">
              <svg className={`w-3 h-3 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable details */}
      {isExpanded && (
        <div className="px-3 md:px-4 pb-3 pt-2 border-t border-anthracite-700/50">
          <div className="text-[11px] text-gray-500 mb-2 md:hidden">{translateLeague(prediction.league, i18n.language)}</div>
          <div className="text-[11px] text-gray-400 mb-2">
            <span className="text-gray-500">{t('date')}</span> {formatTime(prediction.date_time, i18n.language)}
          </div>
          <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4 text-xs">
            {type === 'btts' ? (
              <>
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('awayBTTS')} value={`${prediction.away_btts_pct}%`} highlight={prediction.away_btts_pct >= 60} />
                <DetailItem label={t('totalGPG')} value={prediction.total_gpg?.toFixed(2)} highlight={prediction.total_gpg >= 3.0} />
                <DetailItem label={t('homeBTTS')} value={`${prediction.home_btts_pct}%`} highlight={prediction.home_btts_pct >= 60} />
                <DetailItem label={t('h2hBTTS')} value={`${prediction.h2h_btts_pct}%`} />
                <DetailItem label={t('confidence')} value={`${prediction.confidence}%`} />
                <DetailItem label={t('edge')} value={`${prediction.edge?.toFixed(1)}%`} />
              </>
            ) : type === 'draw' ? (
              <>
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('drawProb')} value={`${prediction.draw_prob?.toFixed(1)}%`} highlight />
                <DetailItem label={t('ppgDiff')} value={prediction.ppg_diff?.toFixed(2)} highlight={Math.abs(prediction.ppg_diff || 0) <= 0.3} />
                <DetailItem label={t('drawTendency')} value={prediction.draw_tendency?.toFixed(2)} highlight={prediction.draw_tendency >= 0.30} />
                <DetailItem label={t('oddsDiff')} value={prediction.odds_diff?.toFixed(2)} highlight={prediction.odds_diff <= 1.0} />
                <DetailItem label={t('combGPG')} value={prediction.combined_gpg?.toFixed(2)} />
                <DetailItem label={t('valueEdge')} value={`${prediction.value_edge?.toFixed(1)}%`} />
                <DetailItem label={t('homeOdds')} value={prediction.odds_home?.toFixed(2)} />
                <DetailItem label={t('awayOdds')} value={prediction.odds_away?.toFixed(2)} />
              </>
            ) : type === 'correct_score' ? (
              <>
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('scoreProb')} value={`${prediction.score_probability?.toFixed(1)}%`} highlight />
                <DetailItem label={t('tier')} value={prediction.confidence_tier} highlight={prediction.confidence_tier === 'HIGH'} />
                <DetailItem label={t('expHome')} value={prediction.expected_home_goals?.toFixed(2)} />
                <DetailItem label={t('expAway')} value={prediction.expected_away_goals?.toFixed(2)} />
                <DetailItem label={t('poisson')} value={`${prediction.poisson_score} (${prediction.poisson_prob?.toFixed(1)}%)`} />
                <DetailItem label={t('classifier')} value={`${prediction.classifier_score} (${prediction.classifier_prob?.toFixed(1)}%)`} />
                <DetailItem label={t('agree')} value={prediction.models_agree ? t('yes') : t('no')} highlight={prediction.models_agree} />
                <DetailItem label={t('secondPick')} value={`${prediction.top2_score} (${prediction.top2_prob?.toFixed(1)}%)`} />
                <DetailItem label={t('thirdPick')} value={`${prediction.top3_score} (${prediction.top3_prob?.toFixed(1)}%)`} />
                <DetailItem label={t('bttsAvg')} value={`${prediction.btts_avg}%`} highlight={prediction.btts_avg < 50} />
                <DetailItem label={t('avgGoals')} value={prediction.avg_goals?.toFixed(1)} highlight={prediction.avg_goals < 3.0} />
                <DetailItem label={t('under15')} value={prediction.under15_avg != null ? `${prediction.under15_avg}%` : 'N/A'} highlight={prediction.under15_avg >= 20} />
                <DetailItem label={t('fairOdds')} value={prediction.estimated_fair_odds?.toFixed(2)} />
                <DetailItem label={t('stake')} value={prediction.stake_suggestion} />
              </>
            ) : type === 'over25' ? (
              <>
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('totalGPG')} value={prediction.total_gpg?.toFixed(2)} highlight={prediction.total_gpg >= 3.0} />
                <DetailItem label={t('homeGPG')} value={prediction.home_gpg?.toFixed(2)} highlight={prediction.home_gpg >= 1.2} />
                <DetailItem label={t('awayGPG')} value={prediction.away_gpg?.toFixed(2)} highlight={prediction.away_gpg >= 1.0} />
                <DetailItem label={t('over25Pct')} value={`${prediction.over25_pct?.toFixed(0)}%`} highlight={prediction.over25_pct >= 55} />
                <DetailItem label={t('avgGoals')} value={prediction.avg_goals?.toFixed(2)} highlight={prediction.avg_goals >= 3.0} />
                <DetailItem label={t('confidence')} value={`${prediction.confidence}%`} />
                <DetailItem label={t('edge')} value={`${prediction.edge?.toFixed(1)}%`} />
                <DetailItem label={t('homeCSPct')} value={`${prediction.home_cs_pct?.toFixed(0)}%`} />
                <DetailItem label={t('awayCSPct')} value={`${prediction.away_cs_pct?.toFixed(0)}%`} />
              </>
            ) : type === 'htft' ? (
              <>
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('htftPick')} value={prediction.prediction} highlight />
                <DetailItem label={t('htftProb')} value={`${(prediction.htft_prob || 0).toFixed(1)}%`} highlight={prediction.htft_prob >= 30} />
                <DetailItem label={t('tier')} value={prediction.confidence_tier} highlight={prediction.confidence_tier === 'HIGH' || prediction.confidence_tier === 'VERY HIGH'} />
                <DetailItem label={t('odds')} value={formatOdds(prediction.odds, oddsFormat)} highlight />
                <DetailItem label={t('valueEdge')} value={`${prediction.value_edge?.toFixed(1)}%`} highlight={prediction.value_edge >= 20} />
                <DetailItem label={t('stake')} value={prediction.stake_suggestion} />
                <DetailItem label={t('ppgDiff')} value={prediction.ppg_diff?.toFixed(2)} highlight={Math.abs(prediction.ppg_diff || 0) <= 0.3} />
                <DetailItem label={t('combGPG')} value={prediction.combined_gpg?.toFixed(2)} />
                <DetailItem label={t('homeHtGPG')} value={prediction.home_ht_gpg?.toFixed(2)} />
                <DetailItem label={t('awayHtGPG')} value={prediction.away_ht_gpg?.toFixed(2)} />
                <DetailItem label={t('homeOdds')} value={prediction.odds_home?.toFixed(2)} />
                <DetailItem label={t('awayOdds')} value={prediction.odds_away?.toFixed(2)} />
              </>
            ) : type === 'ultimate' ? (
              <>
                {prediction.schema_version === 4 && (
                  <>
                    <DetailItem label={t('script')} value={t(SCRIPT_KEYS[prediction.script] || 'script')} highlight />
                    <DetailItem label={t('models')} value={(prediction.supporting_models || []).map(m => translateSourceType(m, t)).join(' + ')} />
                    <div className="col-span-full text-[11px] text-gray-500">{t('modelConsensus')}</div>
                  </>
                )}
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('primaryPick')} value={translatePrimaryLabel(prediction.primary_label, prediction.primary_pick, t)} highlight />
                {prediction.schema_version !== 4 && <DetailItem label={t('overlap')} value={prediction.overlap_label} />}
                {prediction.schema_version !== 4 && <DetailItem label={t('hitRate')} value={`${prediction.expected_hit_rate}%`} highlight />}
                {prediction.schema_version !== 4 && <DetailItem label={t('models')} value={`${prediction.model_count} (${(prediction.confirming_models || []).join(', ')})`} />}
                {prediction.draw_data && (
                  <>
                    <DetailItem label={t('drawProb')} value={`${prediction.draw_data.draw_prob?.toFixed(1)}%`} highlight />
                    <DetailItem label={t('ppgDiff')} value={prediction.quality_signals?.ppg_diff?.toFixed(2)} highlight={Math.abs(prediction.quality_signals?.ppg_diff ?? 999) <= 0.3} />
                    <DetailItem label={t('drawTendency')} value={prediction.quality_signals?.draw_tendency?.toFixed(2)} highlight={(prediction.quality_signals?.draw_tendency || 0) >= 0.30} />
                    <DetailItem label={t('oddsDiff')} value={prediction.quality_signals?.odds_diff?.toFixed(2)} highlight={(prediction.quality_signals?.odds_diff ?? 999) <= 1.0} />
                  </>
                )}
                {prediction.cs_data && (
                  <>
                    <DetailItem label={t('csPick')} value={prediction.cs_data.predicted_score} highlight />
                    <DetailItem label={t('csProb')} value={`${prediction.quality_signals?.cs_score_prob?.toFixed(1)}%`} />
                    <DetailItem label={t('agree')} value={prediction.cs_data.models_agree ? t('yes') : t('no')} highlight={prediction.cs_data.models_agree} />
                  </>
                )}
                <DetailItem label={t('homeXG')} value={prediction.home_xg?.toFixed(2)} highlight={prediction.home_xg >= 1.5} />
                <DetailItem label={t('combGPG')} value={prediction.combined_gpg?.toFixed(2)} highlight={prediction.combined_gpg < 2.7} />
                <DetailItem label={t('redFlags')} value={prediction.quality_signals?.red_flag_count ?? '-'} highlight={(prediction.quality_signals?.red_flag_count || 0) === 0} />
              </>
            ) : type === 'ai_picks' ? (
              <>
                <DetailItem label={t('consensus')} value={translateConsensusLevel(prediction.consensus_level, t)} highlight={prediction.consensus_level === 'HIGH_CONVICTION'} />
                <DetailItem label={t('conviction')} value={prediction.conviction_score?.toFixed(1)} highlight={prediction.conviction_score >= 8} />
                <DetailItem label={t('pickType')} value={translatePickType(prediction.pick_type, t)} highlight />
                <DetailItem label={t('source')} value={translateSourceType(prediction.source_type, t)} />
                <DetailItem label={t('agents')} value={(prediction.agents_agreed || []).join(', ')} />
                <DetailItem label={t('odds')} value={prediction.odds?.toFixed(2)} />
                <DetailItem label={t('grade')} value={prediction.composite_grade?.toFixed(1)} />
                <DetailItem label={t('ev')} value={prediction.expected_value} highlight />
                {prediction.key_factors && prediction.key_factors.length > 0 && (
                  <div className="col-span-2 md:col-span-3 mt-2">
                    <span className="text-[10px] text-gray-500">{t('keyFactors')} </span>
                    <span className="text-[10px] text-green-400">{prediction.key_factors.join(' | ')}</span>
                  </div>
                )}
                {prediction.risks && prediction.risks.length > 0 && (
                  <div className="col-span-2 md:col-span-3">
                    <span className="text-[10px] text-gray-500">{t('risks')} </span>
                    <span className="text-[10px] text-orange-400">{prediction.risks.join(' | ')}</span>
                  </div>
                )}
                {prediction.summary && (
                  <div className="col-span-2 md:col-span-3 mt-2 p-2 bg-anthracite-800 rounded">
                    <span className="text-xs text-gray-300">{prediction.summary}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <DetailItem label={t('gradeScore')} value={(prediction.composite_grade || 0).toFixed(1)} highlight />
                <DetailItem label={t('confidence')} value={`${prediction.confidence}%`} />
                <DetailItem label={`${t('home')} %`} value={`${prediction.prob_home}%`} />
                <DetailItem label={`${t('draw')} %`} value={`${prediction.prob_draw}%`} />
                <DetailItem label={`${t('away')} %`} value={`${prediction.prob_away}%`} />
                <DetailItem label={t('ppgDiff')} value={prediction.ppg_diff?.toFixed(2)} />
                <DetailItem label={t('valueEdge')} value={`${prediction.value_edge?.toFixed(1)}%`} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStatBox({ label, value, highlight = false }) {
  return (
    <div className="bg-anthracite-800 rounded px-2 py-1.5 text-center min-w-[44px]">
      <div className="text-[8px] text-gray-500 uppercase leading-none mb-0.5">{label}</div>
      <div className={`text-[11px] font-semibold leading-none stat-number ${highlight ? 'text-accent' : 'text-gray-300'}`}>{value || '-'}</div>
    </div>
  );
}

function GradeBadge({ prediction, type }) {
  const { t } = useTranslation('dashboard');
  const score = prediction.composite_grade || 0;
  const grade = getGradeInfo(score, type, prediction.grade_letter);
  return (
    <div className={`rounded px-2.5 py-1 text-center w-[52px] ${grade.bg}`}>
      <div className="text-[9px] text-gray-500 uppercase tracking-wide">{t('grade')}</div>
      <div className={`stat-number text-sm font-bold ${grade.color}`}>{grade.letter}</div>
    </div>
  );
}

function MiniGradeBadge({ prediction, type }) {
  const { t } = useTranslation('dashboard');
  const score = prediction.composite_grade || 0;
  const grade = getGradeInfo(score, type, prediction.grade_letter);
  return (
    <div className={`rounded px-2 py-1.5 text-center min-w-[44px] ${grade.bg}`}>
      <div className="text-[8px] text-gray-500 uppercase leading-none mb-0.5">{t('grade')}</div>
      <div className={`text-[11px] font-bold leading-none ${grade.color}`}>{grade.letter}</div>
    </div>
  );
}

function MiniPredictionBox({ prediction }) {
  const { t } = useTranslation('dashboard');
  const colors = {
    'H': 'bg-blue-500/30 text-blue-400',
    'D': 'bg-gray-500/30 text-gray-400',
    'A': 'bg-purple-500/30 text-purple-400',
  };
  const labels = { 'H': t('home'), 'D': t('drawShort'), 'A': t('away') };
  return (
    <div className={`rounded px-2 py-1.5 text-center min-w-[44px] ${colors[prediction] || 'bg-anthracite-800 text-gray-400'}`}>
      <div className="text-[8px] uppercase leading-none mb-0.5">{t('pick')}</div>
      <div className="text-[11px] font-bold leading-none">{labels[prediction] || prediction}</div>
    </div>
  );
}

function StatusBadge({ isPostponed, isLive, isFinished, isWin, dateTime, lang, elapsedMinutes, matchTime, status }) {
  const { t } = useTranslation('dashboard');
  if (isPostponed) return <span className="text-xs font-medium text-yellow-500">{t('postponed')}</span>;
  if (isLive) {
    const isHalfTime = status === 'HALF_TIME' || status === 'HALFTIME';
    const timeDisplay = isHalfTime
      ? 'H/T'
      : matchTime || (elapsedMinutes ? `${elapsedMinutes}'` : t('live'));
    return (
      <div className="flex flex-col items-start">
        <span className="text-[9px] font-bold text-white uppercase tracking-wider leading-none mb-0.5">LIVE</span>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
          <span className="text-xs font-medium text-white">{timeDisplay}</span>
        </div>
      </div>
    );
  }
  if (isFinished && isWin !== null) return <span className={`text-xs font-medium ${isWin ? 'text-win' : 'text-loss'}`}>{isWin ? t('win') : t('loss')}</span>;
  if (isFinished && isWin === null) return <span className="text-xs font-medium text-gray-400">FT</span>;
  return <span className="text-[11px] leading-tight text-gray-500 whitespace-nowrap">{formatTime(dateTime, lang)}</span>;
}

function ScoreDisplay({ hasScore, isPostponed, isLive, isWin, isFinished, result }) {
  if (hasScore) {
    const colorClass = isLive ? 'text-accent' : (isWin === true) ? 'text-win' : (isWin === false) ? 'text-loss' : 'text-gray-300';
    const homeGoalScored = result?.goalScoredSide === 'home';
    const awayGoalScored = result?.goalScoredSide === 'away';
    const hasGoal = homeGoalScored || awayGoalScored;
    return (
      <span className="inline-flex flex-col items-center">
        <span className={`stat-number text-lg font-bold ${colorClass}`}>
          <span className={homeGoalScored ? 'goal-flash-text' : ''}>{result.homeGoals}</span>
          {' - '}
          <span className={awayGoalScored ? 'goal-flash-text' : ''}>{result.awayGoals}</span>
        </span>
        {hasGoal && <span className="goal-text-fade text-[9px] text-white font-bold -mt-1">GOAL!</span>}
      </span>
    );
  }
  if (isPostponed) return <span className="text-sm text-yellow-500">PPD</span>;
  return <span className="text-sm text-gray-600">-</span>;
}

function StatPill({ label, value, highlight = false }) {
  return (
    <div className="text-center w-[52px]">
      <div className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`stat-number text-sm ${highlight ? 'text-accent' : 'text-gray-300'}`}>{value || '-'}</div>
    </div>
  );
}

function PredictionPill({ prediction, label }) {
  const { t } = useTranslation('dashboard');
  const colors = {
    'H': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'D': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'A': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };
  const shortLabels = { 'HOME WIN': t('home'), 'AWAY WIN': t('away'), 'DRAW': t('drawShort') };
  return (
    <div className={`w-[52px] py-1 rounded border text-xs font-bold text-center ${colors[prediction] || 'bg-anthracite-700 text-gray-400'}`}>
      {shortLabels[label] || label || prediction}
    </div>
  );
}

function ScorePill({ score }) {
  return (
    <div className="w-[52px] py-1 rounded border text-xs font-bold text-center bg-amber-500/20 text-amber-400 border-amber-500/30">
      {score || '-'}
    </div>
  );
}

function MiniScoreBox({ score }) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="rounded px-2 py-1.5 text-center min-w-[44px] bg-amber-500/20 text-amber-400">
      <div className="text-[8px] uppercase leading-none mb-0.5">{t('score')}</div>
      <div className="text-[11px] font-bold leading-none">{score || '-'}</div>
    </div>
  );
}

function HtftPredictionPill({ prediction }) {
  const { t } = useTranslation('dashboard');
  const htftColors = {
    'H/H': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'A/A': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'D/D': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'D/H': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    'D/A': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    'H/D': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'A/D': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'H/A': 'bg-red-500/20 text-red-400 border-red-500/30',
    'A/H': 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <div className={`min-w-[52px] py-1 px-2 rounded border text-xs font-bold text-center ${htftColors[prediction] || 'bg-anthracite-700 text-gray-400 border-anthracite-600'}`}>
      {prediction || '-'}
    </div>
  );
}

function MiniHtftBox({ prediction }) {
  const { t } = useTranslation('dashboard');
  const htftColors = {
    'H/H': 'bg-blue-500/20 text-blue-400',
    'A/A': 'bg-purple-500/20 text-purple-400',
    'D/D': 'bg-gray-500/20 text-gray-400',
    'D/H': 'bg-teal-500/20 text-teal-400',
    'D/A': 'bg-teal-500/20 text-teal-400',
    'H/A': 'bg-red-500/20 text-red-400',
    'A/H': 'bg-red-500/20 text-red-400',
  };
  return (
    <div className={`rounded px-2 py-1.5 text-center min-w-[44px] ${htftColors[prediction] || 'bg-anthracite-800 text-gray-400'}`}>
      <div className="text-[8px] uppercase leading-none mb-0.5">{t('htft')}</div>
      <div className="text-[11px] font-bold leading-none">{prediction || '-'}</div>
    </div>
  );
}

function PrimaryPickPill({ prediction }) {
  const { t } = useTranslation('dashboard');
  const primary = prediction.primary_pick;
  const label = ultimateLabel(prediction, t);
  const style = (PRIMARY_STYLES[primary] || PRIMARY_STYLES.correct_score).pill;
  return (
    <div className={`min-w-[52px] py-1 px-2 rounded border text-xs font-bold text-center ${style}`}>
      {label}
    </div>
  );
}

function MiniPrimaryPickBox({ prediction }) {
  const { t } = useTranslation('dashboard');
  const primary = prediction.primary_pick;
  const label = ultimateLabel(prediction, t);
  const style = (PRIMARY_STYLES[primary] || PRIMARY_STYLES.correct_score).box;
  return (
    <div className={`rounded px-2 py-1.5 text-center min-w-[44px] ${style}`}>
      <div className="text-[8px] uppercase leading-none mb-0.5">{t('pick')}</div>
      <div className="text-[11px] font-bold leading-none">{label}</div>
    </div>
  );
}

function translateConsensusLevel(level, t) {
  const map = {
    'HIGH_CONVICTION': t('consensusHighFull'),
    'CONSENSUS': t('consensusConsFull'),
    'SPLIT': t('consensusSplitFull'),
    'ANALYST_PICK': t('consensusPickFull'),
  };
  return map[level] || level?.replace('_', ' ') || '?';
}

function ConsensusBadge({ level }) {
  const { t } = useTranslation('dashboard');
  const styles = {
    'HIGH_CONVICTION': 'bg-emerald-500/30 text-emerald-400 border-emerald-500/50',
    'CONSENSUS': 'bg-blue-500/30 text-blue-400 border-blue-500/50',
    'SPLIT': 'bg-orange-500/30 text-orange-400 border-orange-500/50',
    'ANALYST_PICK': 'bg-purple-500/30 text-purple-400 border-purple-500/50',
  };
  const labels = {
    'HIGH_CONVICTION': t('consensusHigh'),
    'CONSENSUS': t('consensusCons'),
    'SPLIT': t('consensusSplit'),
    'ANALYST_PICK': t('consensusPick'),
  };
  return (
    <div className={`px-2 py-1 rounded border text-xs font-bold ${styles[level] || 'bg-gray-500/20 text-gray-400'}`}>
      {labels[level] || level}
    </div>
  );
}

function MiniConsensusBadge({ level }) {
  const { t } = useTranslation('dashboard');
  const styles = {
    'HIGH_CONVICTION': 'bg-emerald-500/20 text-emerald-400',
    'CONSENSUS': 'bg-blue-500/20 text-blue-400',
    'SPLIT': 'bg-orange-500/20 text-orange-400',
    'ANALYST_PICK': 'bg-purple-500/20 text-purple-400',
  };
  const labels = {
    'HIGH_CONVICTION': t('consensusHigh'),
    'CONSENSUS': t('consensusCons'),
    'SPLIT': t('consensusSplit'),
    'ANALYST_PICK': t('consensusPick'),
  };
  return (
    <div className={`rounded px-2 py-1.5 text-center ${styles[level] || 'bg-gray-500/20'}`}>
      <div className="text-[11px] font-bold leading-none">{labels[level] || '?'}</div>
    </div>
  );
}

function AiPickTypePill({ prediction, t: tProp }) {
  const { t: tHook } = useTranslation('dashboard');
  const t = tProp || tHook;
  const pickType = translatePickType(prediction.pick_type, t);
  const sourceStyles = {
    'btts': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'outcome': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'draw': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'correct_score': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'over25': 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    'ultimate': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  };
  const style = sourceStyles[prediction.source_type] || 'bg-gray-500/20 text-gray-400';
  return (
    <div className={`w-[60px] py-1 px-1 rounded border text-[10px] font-bold text-center truncate ${style}`}>
      {pickType}
    </div>
  );
}

function MiniAiPickBox({ prediction, t: tProp }) {
  const { t: tHook } = useTranslation('dashboard');
  const t = tProp || tHook;
  const pickType = translatePickType(prediction.pick_type, t);
  const sourceLabel = translateSourceType(prediction.source_type, t);
  const sourceStyles = {
    'btts': 'bg-blue-500/20 text-blue-400',
    'outcome': 'bg-orange-500/20 text-orange-400',
    'draw': 'bg-cyan-500/20 text-cyan-400',
    'correct_score': 'bg-amber-500/20 text-amber-400',
    'over25': 'bg-sky-500/20 text-sky-400',
    'ultimate': 'bg-violet-500/20 text-violet-400',
  };
  const style = sourceStyles[prediction.source_type] || 'bg-gray-500/20';
  return (
    <div className={`rounded px-2 py-1.5 text-center min-w-[44px] ${style}`}>
      <div className="text-[8px] uppercase leading-none mb-0.5">{sourceLabel}</div>
      <div className="text-[10px] font-bold leading-none">{pickType}</div>
    </div>
  );
}

function DetailItem({ label, value, highlight = false }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <span className="text-gray-500">{label}:</span>
      <span className={`stat-number ${highlight ? 'text-accent font-medium' : 'text-gray-300'}`}>{value || '-'}</span>
    </div>
  );
}

function checkWin(prediction, type) {
  if (!prediction.result || prediction.result.status !== 'FINISHED') return null;
  const { homeGoals, awayGoals } = prediction.result;
  if (type === 'btts') return homeGoals > 0 && awayGoals > 0;
  if (type === 'draw') return homeGoals === awayGoals;
  if (type === 'over25') return homeGoals + awayGoals >= 3;
  if (type === 'htft') {
    const pred = prediction.prediction || '';
    if (pred.includes('/')) {
      const [predHt, predFt] = pred.split('/');
      const actualFt = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
      // If FT doesn't match, it's definitely a loss regardless of HT
      if (predFt !== actualFt) return false;
      // Check HT result if available
      const htHome = prediction.result.htHomeGoals;
      const htAway = prediction.result.htAwayGoals;
      if (htHome != null && htAway != null) {
        const actualHt = htHome > htAway ? 'H' : htHome < htAway ? 'A' : 'D';
        return predHt === actualHt;
      }
      // No HT data but FT matches — can't determine, show as pending
      return null;
    }
    return false;
  }
  if (type === 'correct_score') return `${homeGoals}-${awayGoals}` === prediction.predicted_score;
  if (type === 'ultimate') {
    const v4Result = settleUltimateV4(prediction, homeGoals, awayGoals);
    if (v4Result !== undefined) return v4Result;
    // Single-pick win logic: judged on primary pick only
    if (prediction.primary_pick === 'draw') return homeGoals === awayGoals;
    if (prediction.primary_pick === 'correct_score') {
      const ps = prediction.cs_data?.predicted_score || prediction.primary_detail;
      return `${homeGoals}-${awayGoals}` === ps;
    }
    // Legacy fallback for old-format picks
    let wins = 0, total = 0;
    if (prediction.btts_prediction) { total++; if (homeGoals > 0 && awayGoals > 0) wins++; }
    if (prediction.outcome_prediction) {
      total++;
      const actual = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
      if (prediction.outcome_prediction.prediction === actual) wins++;
    }
    if (prediction.draw_prediction) { total++; if (homeGoals === awayGoals) wins++; }
    if (prediction.cs_prediction) {
      total++;
      if (`${homeGoals}-${awayGoals}` === prediction.cs_prediction.predicted_score) wins++;
    }
    return total > 0 && wins >= Math.ceil(total / 2);
  }
  if (type === 'ai_picks') {
    // AI picks win based on source_type
    const st = prediction.source_type;
    if (st === 'btts') return homeGoals > 0 && awayGoals > 0;
    if (st === 'draw') return homeGoals === awayGoals;
    if (st === 'over25') return homeGoals + awayGoals >= 3;
    if (st === 'htft') {
      const rawPred = (prediction.pick_type || '').split('(')[0].trim();
      if (rawPred.includes('/')) {
        const [predHt, predFt] = rawPred.split('/').map(s => s.trim());
        const actualFt = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
        const actualHt = prediction.result?.htHomeGoals != null && prediction.result?.htAwayGoals != null
          ? (prediction.result.htHomeGoals > prediction.result.htAwayGoals ? 'H' : prediction.result.htHomeGoals < prediction.result.htAwayGoals ? 'A' : 'D')
          : null;
        if (actualHt === null) return null;
        return predHt === actualHt && predFt === actualFt;
      }
      return false;
    }
    if (st === 'correct_score') {
      const score = prediction.pick_type?.replace('CS ', '') || '';
      return `${homeGoals}-${awayGoals}` === score;
    }
    if (st === 'ultimate') {
      // Check based on pick_type
      if (prediction.pick_type === 'Draw') return homeGoals === awayGoals;
      if (prediction.pick_type?.startsWith('CS')) {
        const score = prediction.pick_type.replace('CS ', '').replace('CS: ', '');
        return `${homeGoals}-${awayGoals}` === score;
      }
    }
    // outcome
    const actual = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
    const pickMap = { 'Home Win': 'H', 'Away Win': 'A', 'Draw': 'D' };
    return pickMap[prediction.pick_type] === actual;
  }
  const actualResult = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
  return prediction.prediction === actualResult;
}

function getTagStyle(tag) {
  const styles = {
    'ML': 'bg-blue-500/20 text-blue-400',
    'H2H': 'bg-purple-500/20 text-purple-400',
    'GPG': 'bg-orange-500/20 text-orange-400',
    'VETO': 'bg-green-500/20 text-green-400',
    'FAV': 'bg-purple-500/20 text-purple-400',
    'HOME': 'bg-orange-500/20 text-orange-400',
    'UPSET': 'bg-red-500/20 text-red-400',
    'FORM': 'bg-teal-500/20 text-teal-400',
    // Draw-specific tags
    'BAL': 'bg-cyan-500/20 text-cyan-400',
    'CLOSE': 'bg-indigo-500/20 text-indigo-400',
    'DEF': 'bg-slate-500/20 text-slate-400',
    'PROF': 'bg-amber-500/20 text-amber-400',
    'CONV': 'bg-emerald-500/20 text-emerald-400',
    // Correct Score tags
    'SHARP': 'bg-yellow-500/20 text-yellow-400',
    'PREM': 'bg-amber-500/20 text-amber-400',
    'PERF': 'bg-lime-500/20 text-lime-400',
    // Over 2.5 tags
    'O25': 'bg-sky-500/20 text-sky-400',
    // HT/FT tags
    'LEAD': 'bg-blue-500/20 text-blue-400',
    'CBACK': 'bg-red-500/20 text-red-400',
    'DD': 'bg-gray-500/20 text-gray-400',
    // Ultimate tags
    'DRAW+CS': 'bg-emerald-500/20 text-emerald-400',
    'CS+': 'bg-amber-500/20 text-amber-400',
    'DRAW+': 'bg-cyan-500/20 text-cyan-400',
    '3ML': 'bg-violet-500/20 text-violet-400',
    'CLEAN': 'bg-green-500/20 text-green-400',
    'XVAL': 'bg-lime-500/20 text-lime-400',
  };
  return styles[tag] || 'bg-anthracite-700 text-gray-400';
}

function formatOdds(odds, format) {
  if (!odds) return '-';
  if (format === 'fractional') return decimalToFractional(odds);
  return odds.toFixed(2);
}

function decimalToFractional(decimal) {
  if (!decimal || decimal <= 1) return '-';
  const decimalPart = decimal - 1;
  const fractions = [
    [1, 10], [1, 9], [1, 8], [1, 7], [1, 6], [1, 5], [2, 9], [1, 4], [2, 7], [3, 10],
    [1, 3], [4, 11], [2, 5], [4, 9], [1, 2], [8, 15], [4, 7], [8, 13], [4, 6], [8, 11],
    [4, 5], [5, 6], [10, 11], [1, 1], [21, 20], [11, 10], [6, 5], [5, 4], [11, 8],
    [6, 4], [13, 8], [7, 4], [15, 8], [2, 1], [85, 40], [11, 5], [12, 5], [5, 2],
    [13, 5], [14, 5], [3, 1], [16, 5], [17, 5], [7, 2], [18, 5], [4, 1], [9, 2],
    [5, 1], [11, 2], [6, 1], [13, 2], [7, 1], [15, 2], [8, 1], [17, 2], [9, 1],
    [10, 1], [11, 1], [12, 1], [14, 1], [16, 1], [20, 1], [25, 1], [33, 1], [50, 1], [100, 1]
  ];
  let closest = fractions[0];
  let minDiff = Math.abs(decimalPart - closest[0] / closest[1]);
  for (const frac of fractions) {
    const diff = Math.abs(decimalPart - frac[0] / frac[1]);
    if (diff < minDiff) { minDiff = diff; closest = frac; }
  }
  return `${closest[0]}/${closest[1]}`;
}

function getLocale(lang) {
  const map = { en: 'en-GB', es: 'es-ES', el: 'el-GR', de: 'de-DE', it: 'it-IT', tr: 'tr-TR' };
  return map[lang] || 'en-GB';
}

function formatTime(dateTime, lang) {
  if (!dateTime || dateTime === 'TBD') return 'TBD';
  const locale = getLocale(lang);
  try {
    if (dateTime.includes(' - ')) {
      const [datePart, timePart] = dateTime.split(' - ');
      const timeMatch = timePart.match(/(\d{1,2}):(\d{2})(am|pm)/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = timeMatch[2];
        const period = timeMatch[3].toLowerCase();
        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
        const dateObj = new Date(`${datePart} ${formattedTime} GMT`);
        if (!isNaN(dateObj.getTime())) {
          const d = dateObj.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
          const tm = dateObj.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
          return `${d} ${tm}`;
        }
      }
      return dateTime;
    }
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      const d = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      const tm = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      return `${d} ${tm}`;
    }
    return dateTime;
  } catch { return dateTime; }
}

export default PredictionCard;
